<template>
  <div class="team-page">
    <front-page-design-nav theme="light" :show-designs-link="false" />

    <section class="team-hero">
      <h1>The people behind Box21</h1>
      <p>
        We build vision AI for teams in healthcare, industry, government, and research
        who need to go from raw images to deployed, integrated models — fast.
      </p>
    </section>

    <section class="team-grid">
      <article v-for="member in members" :key="member.id" class="member-card">
        <div class="member-card__photo-wrap">
          <img
            :src="member.photo"
            :alt="member.name"
            class="member-card__photo"
            :style="{ objectPosition: member.photoPosition || 'center 15%' }"
          />
        </div>
        <div class="member-card__info">
          <h2>{{ member.name }}</h2>
          <p class="member-card__role">{{ member.role }}</p>
          <p class="member-card__bio">{{ member.bio }}</p>
          <a
            v-if="member.link"
            :href="member.link"
            class="member-card__link"
            target="_blank"
            rel="noopener noreferrer"
          >{{ formatLink(member.link) }}</a>
        </div>
      </article>
    </section>

    <section class="team-cta">
      <h2>Want to work with us?</h2>
      <p>We partner with companies, public organisations, and research groups building vision AI products.</p>
      <div class="team-cta__actions">
        <button type="button" class="btn btn--blue" @click="goToRegister">Get started free</button>
        <a href="mailto:info@box21.ai" class="btn btn--text">Contact us</a>
      </div>
    </section>

    <front-page-design-footer theme="light" />
  </div>
</template>

<script>
import FrontPageDesignNav from '@/components/front_page/designs/FrontPageDesignNav.vue'
import FrontPageDesignFooter from '@/components/front_page/designs/FrontPageDesignFooter.vue'
import { TEAM_MEMBERS } from '@/components/front_page/designs/shared'

export default {
  name: 'TeamPage',
  components: { FrontPageDesignNav, FrontPageDesignFooter },
  data() {
    return { members: TEAM_MEMBERS }
  },
  methods: {
    goToRegister() { this.$router.push({ path: '/login', query: { tab: 'register' } }) },
    formatLink(url) { return url.replace(/^https?:\/\//, '').replace(/\/$/, '') },
  },
}
</script>

<style scoped>
.team-page {
  min-height: 100vh;
  background: #fff;
  color: #1e1919;
  font-family: 'Segoe UI', system-ui, sans-serif;
}

.team-hero {
  max-width: 720px;
  margin: 0 auto;
  padding: 64px 24px 48px;
  text-align: center;
}

.team-hero h1 {
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: 700;
  margin: 0 0 16px;
  letter-spacing: -0.02em;
}

.team-hero p {
  font-size: 1.125rem;
  color: #637282;
  line-height: 1.7;
  margin: 0;
}

.team-grid {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 24px 80px;
  display: grid;
  gap: 48px;
}

.member-card {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 40px;
  align-items: center;
  padding: 32px;
  background: #f7f9fa;
  border-radius: 16px;
}

.member-card__photo-wrap {
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  background: #e8ecf0;
}

.member-card__photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.member-card__info h2 {
  font-size: 1.5rem;
  margin: 0 0 4px;
}

.member-card__role {
  font-size: 1rem;
  font-weight: 600;
  color: #0677b9;
  margin: 0 0 12px;
}

.member-card__bio {
  color: #637282;
  line-height: 1.7;
  margin: 0;
}

.member-card__link {
  display: inline-block;
  margin-top: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0677b9;
  text-decoration: none;
}

.member-card__link:hover {
  text-decoration: underline;
}

.team-cta {
  background: #f7f9fa;
  text-align: center;
  padding: 80px 24px;
}

.team-cta h2 {
  font-size: 1.75rem;
  margin: 0 0 12px;
}

.team-cta p {
  color: #637282;
  margin: 0 0 32px;
}

.team-cta__actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.btn {
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  border-radius: 8px;
  padding: 14px 24px;
  text-decoration: none;
}

.btn--blue {
  background: #0677b9;
  color: #fff;
}

.btn--blue:hover {
  background: #055a8c;
}

.btn--text {
  background: transparent;
  color: #0677b9;
  text-decoration: underline;
}

@media (max-width: 640px) {
  .member-card {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .member-card__photo-wrap {
    max-width: 200px;
    margin: 0 auto;
  }
}
</style>
